import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db, doc, getDoc, updateDoc } from '../../firebaseConfig';
import { PeriodProvider } from 'components/PeriodContext';
import TotalTradesSummary from "components/TotalTradesSummary";
import WinLossStats from "components/WinLossStats";
import MostProfitableSymbols from "components/MostProfitableSymbols";
import ProfitFactorGauge from "components/ProfitFactorGauge";
import TradeAnalytics from "components/TradeAnalytics";
import RunningNetPL from "components/RunningNetPL";
import WinRatePieChart from "components/WinRatePieChart";
import ScoreGraph from "components/ScoreGraph";
import DeviationGraph from "components/DeviationGraph";
import MonthlyPerformanceSummary from "components/MonthlyPerformanceSummary";
import MonthlyCalendar from "components/MonthlyCalendar";
import CapitalManagement from "components/CapitalManagement";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import TrialUpgradePopup from "components/TrialUpgradePopup";
import TutorialOfferPopup from "components/TutorialOfferPopup";

const Dashboard = ({ userID, initialCapital, userData, trades, tradeAccounts, selectedAccountID, setSelectedAccountID }) => {
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [isTrialPlan, setIsTrialPlan] = useState(false);
  const [showTutorialPopup, setShowTutorialPopup] = useState(false);
  const [showOverallPerformance, setShowOverallPerformance] = useState(true); // Add state here

  console.log('Dashboard props:', { userID, trades, tradeAccounts, selectedAccountID });

  useEffect(() => {
    const auth = getAuth();
    const today = new Date().toISOString().split('T')[0];
    const trialPopupShownDate = sessionStorage.getItem('trialUpgradePopupShown');
    const popupAlreadyShown = trialPopupShownDate === today;

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log("User is signed in with UID:", user.uid);
        const userDocRef = doc(db, 'Users', user.uid);
        const userDoc = await getDoc(userDocRef);
        let hasSeenTutorialPopup = false;

        if (userDoc.exists()) {
          const userData = userDoc.data();
          hasSeenTutorialPopup = userData.hasSeenTutorialPopup || false;
        } else {
          await updateDoc(userDocRef, {
            hasSeenTutorialPopup: false,
            createdAt: user.metadata.creationTime || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        }

        if (!hasSeenTutorialPopup) {
          setTimeout(() => {
            setShowTutorialPopup(true);
          }, 1000);
        }

        try {
          const subscriptionDoc = await getDoc(doc(db, 'subscriptions', user.uid));
          if (subscriptionDoc.exists()) {
            const subscriptionData = subscriptionDoc.data();
            if (subscriptionData.plan === 'trial_plan') {
              setIsTrialPlan(true);
              if (!popupAlreadyShown && hasSeenTutorialPopup) {
                setTimeout(() => {
                  setShowUpgradePopup(true);
                }, 1500);
              }
            }
          }
        } catch (error) {
          console.error("Error checking subscription:", error);
        }
      } else {
        console.log("User is signed out");
      }
    });
  }, []);

  const handleClosePopup = () => {
    setShowUpgradePopup(false);
  };

  const handleTogglePerformanceView = (showOverall) => {
    setShowOverallPerformance(showOverall); // Update state when toggled
  };

  const handleCloseTutorialPopup = async () => {
    setShowTutorialPopup(false);
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, 'Users', user.uid);
      await updateDoc(userDocRef, {
        hasSeenTutorialPopup: true,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar
        tradeAccounts={tradeAccounts}
        selectedAccountID={selectedAccountID}
        setSelectedAccountID={setSelectedAccountID}
      />
      <MDBox py={3}>
        <PeriodProvider>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <MDBox mb={3}>
                <TotalTradesSummary userID={userID} trades={trades} />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <MDBox mb={3}>
                <WinLossStats userID={userID} trades={trades} initialCapital={initialCapital} />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <MDBox mb={3}>
                <MostProfitableSymbols userID={userID} trades={trades} />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <MDBox mb={3}>
                <TradeAnalytics userID={userID} trades={trades} />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={2.5}>
                <MDBox mb={3}>
                  <RunningNetPL userID={userID} trades={trades} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3.25}>
                <MDBox mb={3}>
                  <WinRatePieChart userID={userID} trades={trades} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={3}>
                  <ProfitFactorGauge userID={userID} trades={trades} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3.25}>
                <MDBox mb={3}>
                  <ScoreGraph userID={userID} trades={trades} showOverallPerformance={showOverallPerformance} tradeAccountID={selectedAccountID} tradeAccounts={tradeAccounts}/>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <DeviationGraph userID={userID} trades={trades} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <MonthlyPerformanceSummary userID={userID} trades={trades} tradeAccountID={selectedAccountID} onTogglePerformanceView={handleTogglePerformanceView} tradeAccounts={tradeAccounts}/>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </PeriodProvider>
        <MDBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <MDBox mb={3}>
              <MonthlyCalendar userID={userID} tradeAccountID={selectedAccountID} />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <CapitalManagement userID={userID} initialCapital={initialCapital} trades={trades}  tradeAccountID={selectedAccountID}/>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
      <TutorialOfferPopup 
        open={showTutorialPopup} 
        onClose={handleCloseTutorialPopup} 
      />
      <TrialUpgradePopup 
        isTrialPlan={isTrialPlan}
        open={showUpgradePopup && isTrialPlan} 
        onClose={handleClosePopup} 
      />
    </DashboardLayout>
  );
};

Dashboard.propTypes = {
  userID: PropTypes.string.isRequired,
  initialCapital: PropTypes.number.isRequired,
  userData: PropTypes.object,
  trades: PropTypes.array.isRequired,
  tradeAccounts: PropTypes.array.isRequired,
  selectedAccountID: PropTypes.string,
  setSelectedAccountID: PropTypes.func.isRequired,
};

export default Dashboard;